import React from 'react';
import { isMobileOnly } from 'react-device-detect';
import { Box, IconButton, Stack, type SxProps } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MenuIcon from '@mui/icons-material/Menu';
import AppLogo from '@components/AppLogo';
import DashboardBarButton from './DashboardBarButton';
import ParticipantsButton from './ParticipantsButton';
import FullScreenButton from './FullScreenButton';
import UserAvatarButton from '@components/UserAvatarButton';
import AdminTools from './AdminTools';
import MytaverseAppBar, {
  AppBarPositionEnum,
} from '@components/MytaverseAppBar';
import { EventDrawerTabs } from '../../../constants';
import { IDashboardTopBarContentView } from './interfaces';
import { useMytaverse } from '@providers/MytaverseProvider';
import ParticipantScopes from '@constants/scopes';
import { getClientSpecificDashboardTopBarRight } from '@helpers/clientSpecificUtils';

import { env } from 'env';

import { useStyles } from './styles';

const {
  REACT_APP_SHOW_LEFT_BAR,
  REACT_APP_SHOW_PROFILE,
  REACT_APP_SHOW_ADMIN_TOOLS,
  REACT_APP_EXTERNAL_EVENT_TAG,
} = env;

const DashboardTopBarContentView = ({
  currentRoom,
  isOpenChat,
  hasMediaStream,
  participantTotal,
  onOpenEventDrawer,
  currentEvent,
}: IDashboardTopBarContentView) => {
  const classes = useStyles({
    isOpenChat,
    hasMediaStream,
  });
  const { userPermissions } = useMytaverse();
  const showLeftMenu = currentEvent?.enablePureGamecast || currentRoom;
  const showAdminTools = REACT_APP_SHOW_ADMIN_TOOLS === 'true';
  const showLeftBar = REACT_APP_SHOW_LEFT_BAR === 'true';
  const showProfile =
    REACT_APP_SHOW_PROFILE === 'true' ||
    (REACT_APP_EXTERNAL_EVENT_TAG
      ? currentEvent?.eventTags?.includes(REACT_APP_EXTERNAL_EVENT_TAG)
      : false);

  //TODO: when it's active, it should be added to mobile as well
  const isAdminToolEnabled =
    showAdminTools &&
    userPermissions?.includes(ParticipantScopes.HubAdminTools);
  const allowOnMobileForExternalEvent =
    !!REACT_APP_EXTERNAL_EVENT_TAG &&
    currentEvent?.eventTags?.includes(REACT_APP_EXTERNAL_EVENT_TAG);

  if (isMobileOnly && !allowOnMobileForExternalEvent) {
    return null;
  }

  const renderTopRightActions = (customSx?: SxProps) => (
    <MytaverseAppBar
      position={AppBarPositionEnum.TopRight}
      isCollapsible={false}
      sx={customSx}
    >
      <FullScreenButton />
      {isAdminToolEnabled ? <AdminTools /> : null}
      {showProfile ? <UserAvatarButton /> : null}
    </MytaverseAppBar>
  );

  const clientSpecificDashboardTopBar = getClientSpecificDashboardTopBarRight(
    renderTopRightActions,
  );

  return (
    <>
      {showLeftBar ? (
        <MytaverseAppBar
          position={AppBarPositionEnum.TopLeft}
          sx={classes.leftBar}
          isCollapsible={false}
          isGroup={false}
        >
          {showLeftMenu ? (
            <>
              <Box sx={classes.root} component="div">
                <DashboardBarButton
                  buttonIcon={<LocationOnIcon />}
                  onClick={() => onOpenEventDrawer(EventDrawerTabs.Locations)}
                >
                  {currentRoom?.name || 'Lobby'}
                </DashboardBarButton>
                <ParticipantsButton
                  onClick={() =>
                    onOpenEventDrawer(EventDrawerTabs.Participants)
                  }
                  total={participantTotal}
                />
              </Box>
              <IconButton
                sx={classes.iconButton}
                onClick={() => onOpenEventDrawer(EventDrawerTabs.Locations)}
              >
                <MenuIcon />
              </IconButton>
            </>
          ) : (
            <Box sx={classes.rootLoading}>
              <Stack flexDirection="row" alignItems="center">
                <AppLogo />
              </Stack>
            </Box>
          )}
        </MytaverseAppBar>
      ) : null}
      {clientSpecificDashboardTopBar
        ? clientSpecificDashboardTopBar
        : renderTopRightActions()}
    </>
  );
};

export default DashboardTopBarContentView;
